import React, { useRef, useState, useEffect } from "react";
import styles from "./index.scss";
import type { IFormRef } from "./form";
import Button from "@m4b-design/button";
import cx from "classnames";
import loadable from "@loadable/component";
import type { IDetailProps } from "../detail";
import { getShopRegion } from "@oec-seller/sdk/hooks";
import { useTranslation } from "@/i18n";
import Layout from "@/layout";
import { CreateFormField } from "./constants";
import { LayoutLeft } from "@/components/diffComponents";
import Callout from "@m4b-design/callout";
import Space from "@m4b-design/space";
import { isUSRegion } from "@/utils";
import { useTeaReport } from "@/hooks/useTeaReport";
import { SELLER_EMAIL_WEBFORM } from "@/constants/logger/webform";
import { isCrossBorderSite } from "./runtime";
import { getRegionRedirectUrl, getSellerLoginUrl } from "./routing";

const Detail = loadable(() => import("../detail"));
const CreateForm = loadable(() => import("./form"), {
  resolveComponent: (m) => m.CreateForm,
  fallback: <div className="flex-1" />,
  forwardRef: true,
});

/* eslint-disable @typescript-eslint/explicit-function-return-type */

enum EPageType {
  Form,
  TicketDetail,
}

const Create = (): React.ReactElement => {
  const formRef = useRef<IFormRef>({});
  const [pageType, setPageType] = useState(EPageType.Form);
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState<IDetailProps>({
    region: "",
    topic: "",
    detail: "",
    issues: "",
    email: "",
    name: "",
    files: [],
    ticketNum: "",
  });
  const { t } = useTranslation("i18n_ecom_shop_email");
  const trackEvent = useTeaReport();

  useEffect(() => {
    const { hostname, pathname, search, hash } = window.location;
    const redirectUrl = getRegionRedirectUrl(
      { hostname, pathname, search, hash },
      getShopRegion
    );
    if (redirectUrl) {
      window.location.href = redirectUrl;
    }
    trackEvent.view(
      {
        pageName: "ticket_email_webofrm_page",
        section: "",
        target: "",
      },
      {
        isCB: isCrossBorderSite,
      }
    );
  }, [trackEvent]);

  const handleSubmit = async () => {
    const data = await formRef.current.submit?.(setLoading);
    setDetail(data as IDetailProps);
    setPageType(EPageType.TicketDetail);
  };

  const handleLangChange = (): Promise<boolean> =>
    new Promise((resolve) => {
      const form = formRef?.current?.form;
      if (form?.getFieldValue?.(CreateFormField.Topic)) {
        const canContinue = window.confirm(
          t(
            "contact_us_change_region_dialog_title",
            "The topic and issue will be cleared"
          ) as string
        );
        if (canContinue) {
          form.setFieldsValue({
            [CreateFormField.Topic]: undefined,
            [CreateFormField.Detail]: undefined,
          });
          resolve(true);
        } else {
          resolve(false);
        }
      } else {
        resolve(true);
      }
    });

  const toLogin = () => {
    const hostname = window.location.hostname;
    trackEvent.click(SELLER_EMAIL_WEBFORM.CLICK_LOGIN_BANNER);
    window.location.href = getSellerLoginUrl(
      hostname,
      isCrossBorderSite || isUSRegion
    );
  };

  const renderTips = () => {
    const notes = t(
      "login_contactus_emailwebform_notes",
      "Only general inquiries can be supported through this form. If you have an active account, please log in to Seller Center to ensure our live agents can access your account details to best support you. If you're unable to log in to your account, contact us using the form below."
    );
    const loginText = t(
      "login_contactus_emailwebform_loginlink",
      "Seller Center"
    );
    const notesTextList = notes.split(loginText);
    const notesText = notesTextList.map((item, index) => (
      <React.Fragment key={item}>
        {item}
        {index === notesTextList.length - 1 ? null : (
          <Button type="link" onClick={toLogin}>
            {loginText}
          </Button>
        )}
      </React.Fragment>
    ));
    return (
      <div style={{ width: "100%" }}>
        <p>{notesText}</p>
      </div>
    );
  };

  return (
    <Layout onLangChange={handleLangChange}>
      <div className={cx(styles.container, " flex flex-col")}>
        {pageType === EPageType.Form && (
          <>
            <div
              className="text-neutral-text1 mb-16 text-head-xl"
              data-testid="ticket-email-title"
            >
              {t("contact_us_page_title", "Contact Us") as string}
            </div>
            <Space style={{ padding: "16px 0" }}>
              <Callout
                expanded={true}
                expandMode={false}
                closable={false}
                type="warning"
                content={renderTips()}
              />
            </Space>
            <div className="bg-white w-full rounded-8 flex items-start justify-start p-24 pb-40">
              <LayoutLeft />
              <CreateForm ref={formRef} />
            </div>
            <Button
              type="primary"
              className="self-end mt-16"
              onClick={handleSubmit}
              loading={loading}
              data-testid="ticket-email-submit"
            >
              {t("seller_center_submit_common", "Submit") as string}
            </Button>
          </>
        )}
        {pageType === EPageType.TicketDetail && <Detail {...detail} />}
      </div>
    </Layout>
  );
};

export default Create;
